import {
  ConfirmDialog,
  getPeople,
  InvalidInput,
  invite,
  NotFound,
  Pick,
  removeMember,
  revokeInvite,
  setRole,
  type Member,
  type People,
  type Role,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { day, problemKey } from '../../lib/format';
import { Copyable } from './Copyable';
import { ShareLinkPanel } from './ShareLinkPanel';
import { useReadOnly } from '../../app/readOnly';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; people: People };

/** A link just made, to copy and pass on: an invitation not e-mailed. */
interface Made {
  kind: 'invite';
  link: string;
  email?: string;
}

/**
 * Who sees a trip, the bar's Share: its members and their roles, the invitations out, the
 * read-only link and what each role may do. Only the owner changes any of it; an editor sees it.
 */
export function Sharing() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [role, setRoleChoice] = useState<Role>('editor');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [made, setMade] = useState<Made | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [leaving, setLeaving] = useState<Member | null>(null);
  const locked = useReadOnly();

  useEffect(() => {
    getPeople(id).then(
      (people) => {
        setState({ kind: 'ready', people });
      },
      (error: unknown) => {
        setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
  }, [id]);

  if (state.kind === 'loading') {
    return <p className="page quiet">{t('trips.loading')}</p>;
  }
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }

  const { people } = state;
  // Its owner manages who is on it; on a trip read only, no one: who is on it only said.
  const owner = people.role === 'owner' && !locked;
  const roleOptions = (['owner', 'editor', 'viewer'] as const).map((value) => ({
    value,
    label: t(`account.role.${value}`),
  }));
  const updated = (job: Promise<People>) => {
    setFailed(false);
    job.then(
      (next) => {
        setState({ kind: 'ready', people: next });
      },
      (error: unknown) => {
        setFailed(!(error instanceof InvalidInput));
        if (error instanceof InvalidInput) setErrors({ role: t('group.lastOwner') });
      },
    );
  };
  const reload = () => {
    getPeople(id).then(
      (next) => {
        setState({ kind: 'ready', people: next });
      },
      () => {
        setFailed(true);
      },
    );
  };
  const send = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setErrors({});
    setFailed(false);
    setMade(null);
    setSent(null);
    invite(id, email, role).then(
      (answer) => {
        if (answer.sent) setSent(answer.invite.email);
        else setMade({ kind: 'invite', link: answer.link, email: answer.invite.email });
        setEmail('');
        reload();
      },
      (error: unknown) => {
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
          );
        } else {
          setFailed(true);
        }
      },
    );
  };
  return (
    <div className="group-page">
      <h1 className="visually-hidden">{t('sharing.title')}</h1>

      {failed && (
        <p className="form-failed" role="alert">
          {t('plan.failed')}
        </p>
      )}

      <div className="group-columns">
        <div className="sharing-main">
          <section className="panel" aria-labelledby={`${ids}-people`}>
            <h2 id={`${ids}-people`}>{t('sharing.members')}</h2>
            <ul className="people">
              {people.members.map((member) => (
                <li key={member.userId}>
                  <span className="person">
                    <span className="avatar" aria-hidden="true">
                      {member.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span>
                      <strong>{member.name}</strong>
                      <span className="quiet person-email">{member.email}</span>
                    </span>
                  </span>
                  {owner ? (
                    <span className="person-actions">
                      <Pick
                        label={t('group.roleOf', { name: member.name })}
                        quiet
                        options={roleOptions}
                        value={member.role}
                        searchable={false}
                        onChange={(next) => {
                          setErrors({});
                          updated(setRole(id, member.userId, next));
                        }}
                      />
                      <button
                        type="button"
                        className="button button-quiet"
                        aria-label={t('group.remove', { name: member.name })}
                        onClick={() => {
                          setLeaving(member);
                        }}
                      >
                        {t('group.removeShort')}
                      </button>
                    </span>
                  ) : (
                    <span className="role-tag">{t(`account.role.${member.role}`)}</span>
                  )}
                </li>
              ))}
            </ul>
            {errors.role !== undefined && <p className="field-error">{errors.role}</p>}

            {people.invites.length > 0 && (
              <>
                <h3>{t('group.invited')}</h3>
                <ul className="people">
                  {people.invites.map((invitation) => (
                    <li key={invitation.id}>
                      <span>
                        <strong>{invitation.email}</strong>
                        <span className="quiet person-email">
                          {t('group.invitedAs', {
                            role: t(`account.role.${invitation.role}`),
                            until: day(invitation.expires.slice(0, 10), lang),
                          })}
                        </span>
                      </span>
                      {owner && (
                        <button
                          type="button"
                          className="button button-quiet"
                          aria-label={t('group.withdraw', { email: invitation.email })}
                          onClick={() => {
                            updated(revokeInvite(id, invitation.id));
                          }}
                        >
                          {t('group.withdrawShort')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {owner && (
              <form className="form invite-form" onSubmit={send} noValidate>
                <h3>{t('group.invite')}</h3>
                <div className="form-row">
                  <div className="field">
                    <label className="field-label" htmlFor={`${ids}-email`}>
                      {t('account.email')}
                    </label>
                    <input
                      id={`${ids}-email`}
                      className="text-input"
                      type="email"
                      autoComplete="off"
                      value={email}
                      aria-invalid={errors.email !== undefined}
                      aria-describedby={
                        errors.email === undefined ? undefined : `${ids}-email-error`
                      }
                      onChange={(event) => {
                        setEmail(event.target.value);
                      }}
                    />
                    {errors.email !== undefined && (
                      <p className="field-error" id={`${ids}-email-error`}>
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <Pick
                    label={t('group.as')}
                    options={roleOptions.slice(1)}
                    value={role}
                    searchable={false}
                    onChange={setRoleChoice}
                  />
                </div>
                <p className="quiet">{t(people.mail ? 'group.byMail' : 'group.noMail')}</p>
                <button type="submit" className="button button-primary">
                  {t(people.mail ? 'group.send' : 'group.makeInvite')}
                </button>
                {sent !== null && (
                  <p className="added" role="status">
                    {t('group.sent', { email: sent })}
                  </p>
                )}
                {made?.kind === 'invite' && (
                  <div className="added" role="status">
                    <p>{t('group.sendYourself', { email: made.email })}</p>
                    <Copyable label={t('group.inviteLink')} link={made.link} />
                  </div>
                )}
              </form>
            )}
          </section>
        </div>
        <div className="group-right">
          <ShareLinkPanel trip={id} owner={owner} since={people.shared} />
          <Roles />
        </div>
      </div>

      {leaving && (
        <ConfirmDialog
          open
          title={t('group.removeQuestion', { name: leaving.name })}
          body={t('group.removeBody')}
          confirmLabel={t('group.removeConfirm')}
          onCancel={() => {
            setLeaving(null);
          }}
          onConfirm={() => {
            setLeaving(null);
            updated(removeMember(id, leaving.userId));
          }}
        />
      )}
    </div>
  );
}

/** Who can do what on a trip (docs/02 §5), as the Group mockup lists it. */
function Roles() {
  const { t } = useTranslation();
  const rows = [
    ['route', 'yes', 'yes', 'no'],
    ['money', 'yes', 'yes', 'no'],
    ['documents', 'yes', 'yes', 'no'],
    ['people', 'yes', 'no', 'no'],
  ] as const;
  return (
    <section className="panel roles-card" aria-labelledby="roles-title">
      <h2 id="roles-title">{t('group.rolesTitle')}</h2>
      <table>
        <thead>
          <tr>
            <td />
            <th scope="col">{t('account.role.owner')}</th>
            <th scope="col">{t('account.role.editor')}</th>
            <th scope="col">{t('account.role.viewer')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([what, ...may]) => (
            <tr key={what}>
              <th scope="row">{t(`group.can.${what}`)}</th>
              {may.map((answer, at) => (
                <td key={at} className={answer === 'no' ? 'quiet' : undefined}>
                  {t(`group.${answer}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
